import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { useAdminStore } from "@/features/auth/stores/adminStore";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  User,
  LogOut,
  CreditCard,
  BarChart3,
  Menu,
  X,
  ClipboardList,
  History,
  Bug,
} from "lucide-react";
import { useState, createContext, useContext, useMemo } from "react";
import { Breadcrumbs, type BreadcrumbItem } from "../ui/Breadcrumbs";
import { FloatingBugButton } from "../FloatingBugButton";
import { ProfileCompletionBanner } from "../ProfileCompletionBanner";
import { NotificationBell, NotificationToastContainer } from "@/features/notifications";

// Breadcrumb context for pages to set their breadcrumbs
interface BreadcrumbContextValue {
  items: BreadcrumbItem[];
  setItems: (items: BreadcrumbItem[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function useBreadcrumbs() {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbs must be used within DashboardLayout");
  }
  return context;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface DashboardLayoutProps {
  isAdmin?: boolean;
}

export function DashboardLayout({ isAdmin = false }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { advisor, logout: advisorLogout } = useAdvisorStore();
  const { admin, logout: adminLogout } = useAdminStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);

  // Memoize breadcrumb context value to prevent unnecessary re-renders
  const breadcrumbContextValue = useMemo(
    () => ({
      items: breadcrumbItems,
      setItems: setBreadcrumbItems,
    }),
    [breadcrumbItems]
  );

  const advisorNavItems: NavItem[] = [
    { path: "/", label: "Dashboard", icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: "/leads", label: "Leady", icon: <Users className="w-5 h-5" /> },
    { path: "/sessions", label: "Sesje", icon: <MessageSquare className="w-5 h-5" /> },
    { path: "/templates", label: "Szablony", icon: <ClipboardList className="w-5 h-5" /> },
    { path: "/tickets", label: "Moje zgłoszenia", icon: <Bug className="w-5 h-5" /> },
    { path: "/profile", label: "Profil", icon: <User className="w-5 h-5" /> },
  ];

  const adminNavItems: NavItem[] = [
    { path: "/admin", label: "Dashboard", icon: <BarChart3 className="w-5 h-5" /> },
    { path: "/admin/advisors", label: "Doradcy", icon: <Users className="w-5 h-5" /> },
    { path: "/admin/audit-logs", label: "Historia", icon: <History className="w-5 h-5" /> },
  ];

  const navItems = isAdmin ? adminNavItems : advisorNavItems;
  const user = isAdmin ? admin : advisor;
  const handleLogout = () => {
    if (isAdmin) {
      adminLogout();
      navigate("/admin/login");
    } else {
      advisorLogout();
      navigate("/login");
    }
  };

  const isActive = (path: string) => {
    if (path === "/" || path === "/admin") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <BreadcrumbContext.Provider value={breadcrumbContextValue}>
      <div className="min-h-screen bg-gray-50">
        {/* Skip link for keyboard navigation */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 z-[60] bg-primary-600 text-white px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Przejdz do glownej tresci
        </a>

        {/* Mobile menu button */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <Link to={isAdmin ? "/admin" : "/"} className="text-xl font-bold text-primary-600">
            {isAdmin ? "Panel Admin" : "Panel Doradcy"}
          </Link>
          <div className="flex items-center gap-2">
            {!isAdmin && <NotificationBell />}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Desktop header bar */}
        <div className="hidden lg:flex fixed top-0 left-64 right-0 z-30 bg-white border-b border-gray-200 px-6 py-3 items-center justify-end gap-4">
          {!isAdmin && <NotificationBell />}
          <div className="text-sm text-gray-600">
            {user?.name || user?.email}
          </div>
        </div>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 w-64 h-full bg-white border-r border-gray-200 transition-transform lg:translate-x-0 ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="hidden lg:flex items-center h-16 px-6 border-b border-gray-200">
            <Link to={isAdmin ? "/admin" : "/"} className="text-xl font-bold text-primary-600">
              {isAdmin ? "Panel Admin" : "Panel Doradcy"}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 mt-14 lg:mt-0">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200">
            {!isAdmin && advisor && (
              <div className="mb-4 p-3 bg-primary-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CreditCard className="w-4 h-4" />
                  <span>Kredyty</span>
                </div>
                <div className="text-lg font-semibold text-primary-700">
                  {advisor.credits}
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {user?.name || user?.email}
                </div>
                <div className="text-sm text-gray-500 truncate">{user?.email}</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Wyloguj
            </button>
          </div>
        </div>
      </aside>

        {/* Main content */}
        <main
          id="main-content"
          className="lg:ml-64 pt-14 lg:pt-14 min-h-screen"
          tabIndex={-1}
        >
          <div className="p-6">
            {/* Breadcrumbs */}
            {breadcrumbItems.length > 0 && (
              <div className="mb-4">
                <Breadcrumbs items={breadcrumbItems} />
              </div>
            )}

            {/* Profile completion banner (advisors only) */}
            {!isAdmin && <ProfileCompletionBanner />}

            <Outlet />
          </div>
        </main>

        {/* Floating Bug Report Button (only for advisors) */}
        {!isAdmin && <FloatingBugButton />}

        {/* Notification Toasts */}
        {!isAdmin && <NotificationToastContainer />}
      </div>
    </BreadcrumbContext.Provider>
  );
}
